/**
 * The appearance, before anything renders.
 *
 * This lived in an inline <script> in index.html, where `script-src 'self'`
 * blocked it silently: the page loaded light every time regardless of what
 * the person had chosen, and choosing again only lasted until the next
 * reload. A module from this origin is allowed, and it still runs before the
 * first paint because nothing has rendered yet.
 */
try {
  const stored = localStorage.getItem('pathara.theme') ?? 'system';
  const dark =
    stored === 'dark' ||
    (stored === 'system' && matchMedia('(prefers-color-scheme: dark)').matches);
  document.documentElement.dataset['theme'] = dark ? 'dark' : 'light';
} catch {
  document.documentElement.dataset['theme'] = 'light';
}

import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { App } from './app/app.js';
import { AppProviders, createQueryClient } from './app/providers.js';
import { initErrorTracking } from './shared/observability/error-tracking.js';
import './styles/global.css';

/**
 * Browser entry point.
 *
 * Kept to mounting only. Configuration is validated on import of the env
 * module, so a misconfigured build fails here with a readable message instead
 * of rendering a blank page.
 */
const container = document.getElementById('root');
if (container === null) {
  throw new Error('Root container #root is missing from index.html.');
}

initErrorTracking();
const queryClient = createQueryClient();

// The service worker that lets /capture open with no signal. Production only:
// in development it would cache a bundle that changes on every save.
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {
      // The app works without it; it only loses offline opening.
    });
  });
}

createRoot(container).render(
  <StrictMode>
    <AppProviders queryClient={queryClient}>
      <App queryClient={queryClient} />
    </AppProviders>
  </StrictMode>,
);
